import { useCallback, useEffect, useState } from "react";

import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

import ExpandMore from "@mui/icons-material/ExpandMore";

import Radio from "@mui/material/Radio";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ThemeProvider } from "@mui/material";

import BaseGraph from "./BaseGraph";

import { table2Theme } from "../styles/Table2";
import { defaultTheme } from "../styles/Theme";

// #FDF9F2
// #FDF2F2

const records: {
  [type: string]: {
    month: string;
    price: number;
  }[];
} = {
  open: [
    { month: "Jan", price: 0 },
    { month: "Feb", price: 0 },
    { month: "Mar", price: 3000 },
    { month: "Apr", price: 5000 },
  ],
  lost: [
    { month: "Jan", price: 250 },
    { month: "Feb", price: 0 },
    { month: "Mar", price: 500 },
    { month: "Apr", price: 0 },
  ],
  won: [
    { month: "Jan", price: 200 },
    { month: "Feb", price: 0 },
    { month: "Mar", price: 800 },
    { month: "Apr", price: 0 },
  ],
  priorYear: [
    { month: "Jan", price: 1000 },
    { month: "Feb", price: 300 },
    { month: "Mar", price: 1 },
    { month: "Apr", price: 10 },
  ],
};

const VerticalDivider = () => {
  return (
    <div
      style={{
        height: 24,
        width: 2,
        backgroundColor: "#D9D9D9",
        margin: "0px 18px",
        marginTop: -2,
      }}
    />
  );
};

const formatNumber = (num: number) => {
  if (num >= 0) {
    return <div style={{ color: "#009F5C" }}>{num.toLocaleString("en")}</div>;
  }

  return (
    <div style={{ color: "#C52F00" }}>{`(${num.toLocaleString("en")})`}</div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ShipmentsTable = () => {
  const infoMessage = (
    <>
      <b>Filter criteria</b>: Customer Name, Modes, Industry, Cities, States
      <br />
      {/* <b>UOM</b>: {uom} */}
    </>
  );

  const [rows, setRows] = useState<any[]>([]);

  const calculate = useCallback(() => {
    const totals = [
      records.open[0].price + records.lost[0].price + records.won[0].price,
      records.open[1].price + records.lost[1].price + records.won[1].price,
      records.open[2].price + records.lost[2].price + records.won[2].price,
      records.open[3].price + records.lost[3].price + records.won[3].price,
    ];
    totals.push(totals.reduce((a, b) => a + b, 0));

    const newRows = [];

    newRows.push(
      <TableRow style={{ backgroundColor: "#FDF9F2" }}>
        <TableCell />
        <TableCell style={{ fontWeight: "700", color: "#231F20" }}>
          Open
        </TableCell>
        <TableCell>{records.open[0].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.open[1].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.open[2].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.open[3].price.toLocaleString("en")}</TableCell>
        <TableCell>{totals[0]}</TableCell>
      </TableRow>
    );

    newRows.push(
      <TableRow style={{ backgroundColor: "#FDF9F2" }}>
        <TableCell />
        <TableCell style={{ fontWeight: "700", color: "#231F20" }}>
          Lost
        </TableCell>
        <TableCell>{records.lost[0].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.lost[1].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.lost[2].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.lost[3].price.toLocaleString("en")}</TableCell>
        <TableCell>{totals[1]}</TableCell>
      </TableRow>
    );

    newRows.push(
      <TableRow style={{ backgroundColor: "#FDF9F2" }}>
        <TableCell style={{ width: 40, position: "relative" }}>
          <div
            style={{
              paddingLeft: 8,
              position: "absolute",
              top: 0,
              bottom: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ExpandMore style={{ color: "#09B0F2" }} />
          </div>
        </TableCell>
        <TableCell style={{ fontWeight: "700", color: "#231F20", width: 260 }}>
          Won
        </TableCell>
        <TableCell>{records.won[0].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.won[1].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.won[2].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.won[3].price.toLocaleString("en")}</TableCell>
        <TableCell>{totals[2]}</TableCell>
      </TableRow>
    );

    newRows.push(
      <TableRow
        style={{
          backgroundColor: "#FDF9F2",
          fontWeight: "700",
          color: "#231F20",
        }}
      >
        <TableCell />
        <TableCell>Total opportunities</TableCell>
        <TableCell>{totals[0].toLocaleString("en")}</TableCell>
        <TableCell>{totals[1].toLocaleString("en")}</TableCell>
        <TableCell>{totals[2].toLocaleString("en")}</TableCell>
        <TableCell>{totals[3].toLocaleString("en")}</TableCell>
        <TableCell>{totals[4].toLocaleString("en")}</TableCell>
      </TableRow>
    );

    const totalPriorYear = records.priorYear.reduce((a, b) => a + b.price, 0);
    newRows.push(
      <TableRow
        style={{
          backgroundColor: "#FDF2F2",
          fontWeight: 700,
          color: "#231F20",
        }}
      >
        <TableCell />
        <TableCell>Prior year&apos;s average</TableCell>
        <TableCell>{records.priorYear[0].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.priorYear[1].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.priorYear[2].price.toLocaleString("en")}</TableCell>
        <TableCell>{records.priorYear[3].price.toLocaleString("en")}</TableCell>
        <TableCell>{totalPriorYear}</TableCell>
      </TableRow>
    );

    const differences = [
      totals[0] - records.priorYear[0].price,
      totals[1] - records.priorYear[1].price,
      totals[2] - records.priorYear[2].price,
      totals[3] - records.priorYear[3].price,
      totals[4] - totalPriorYear,
    ];

    newRows.push(
      <TableRow style={{ backgroundColor: "#FDF2F2" }}>
        <TableCell />
        <TableCell style={{ fontWeight: 700, color: "#231F20" }}>
          Difference
        </TableCell>
        <TableCell>{formatNumber(differences[0])}</TableCell>
        <TableCell>{formatNumber(differences[1])}</TableCell>
        <TableCell>{formatNumber(differences[2])}</TableCell>
        <TableCell>{formatNumber(differences[3])}</TableCell>
        <TableCell>{formatNumber(differences[4])}</TableCell>
      </TableRow>
    );

    setRows(newRows);
  }, []);

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const body = (
    <ThemeProvider theme={table2Theme}>
      <TableContainer
        // component={Paper}
        style={{ maxHeight: 290, overflowY: "auto" }}
        sx={{ boxShadow: "none" }}
      >
        <Table stickyHeader size="small" aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={2}
                style={{ paddingRight: 50, width: 300, maxWidth: 300 }}
              >
                <ThemeProvider theme={defaultTheme}>
                  <Select
                    className="XtremeSmall"
                    size="small"
                    fullWidth
                    value="now"
                  >
                    <MenuItem value="now">Current year</MenuItem>
                  </Select>
                </ThemeProvider>
              </TableCell>
              <TableCell>Jan</TableCell>
              <TableCell>Feb</TableCell>

              <TableCell>Mar</TableCell>
              <TableCell>Apr</TableCell>
              <TableCell>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            id="tablebody"
            style={{ overflowY: "scroll", cursor: "pointer", color: "#595959" }}
          >
            {rows}
          </TableBody>
        </Table>
      </TableContainer>
    </ThemeProvider>
  );

  const subTitle = (
    <div style={{ display: "flex" }}>
      <div style={{ display: "flex", gap: 14 }}>
        <div style={{ color: "#231F20", fontSize: 14, fontWeight: 700 }}>
          Quantity
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <FormControlLabel label="Monthly" control={<Radio />} />
          <FormControlLabel label="Running total" control={<Radio />} />
        </div>
      </div>

      <VerticalDivider />
      <div style={{ display: "flex", gap: 14 }}>
        <div style={{ color: "#231F20", fontSize: 14, fontWeight: 700 }}>
          Year
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <FormControlLabel label="Calendar" control={<Radio />} />
          <FormControlLabel label="Fertilizer" control={<Radio />} />
        </div>
      </div>

      <VerticalDivider />

      <div style={{ display: "flex", gap: 14 }}>
        <div style={{ color: "#231F20", fontSize: 14, fontWeight: 700 }}>
          Timing
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <FormControlLabel label="Shipment date" control={<Radio />} />
          <FormControlLabel label="Sell date" control={<Radio />} />
        </div>
      </div>

      <VerticalDivider />
      <div style={{ display: "flex", gap: 14 }}>
        <div style={{ color: "#231F20", fontSize: 14, fontWeight: 700 }}>
          Display
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <FormControlLabel label="Table" control={<Radio />} />
          <FormControlLabel label="Chart" control={<Radio />} />
        </div>
      </div>
    </div>
  );

  return (
    <BaseGraph
      // loading={loading}
      loading={false}
      title="SHIPMENTS"
      subTitle={subTitle}
      infoMessage={infoMessage}
      height={300}
    >
      {body}
    </BaseGraph>
  );
};

export default ShipmentsTable;
